package com.karyadina.ui.screens.studyPlanner

import androidx.compose.animation.*
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyListScope
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.nestedscroll.nestedScroll
import androidx.compose.ui.unit.dp
import com.karyadina.ui.screens.studyPlanner.components.* // Imports all your new files
import com.karyadina.ui.components.StudyFloatingActionButton
import com.karyadina.model.StudySession
import com.karyadina.model.Subject
import com.karyadina.ui.screens.studyPlanner.presentation.StudyPlannerEvent
import com.karyadina.ui.screens.studyPlanner.presentation.StudyPlannerViewModel
import com.karyadina.ui.theme.StudyIcons
import com.karyadina.ui.theme.StudyStrings
import com.karyadina.ui.theme.StudyTheme
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.koinInject
import kotlinx.coroutines.launch


@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun StudyPlannerScreen(
    onBackClick: () -> Unit,
    viewModel: StudyPlannerViewModel = remember { StudyPlannerViewModel() }
) {
    val scope = rememberCoroutineScope()

    val state by viewModel.uiState.collectAsState()
    val listState = rememberLazyListState()
    val scrollBehavior = TopAppBarDefaults.exitUntilCollapsedScrollBehavior()

    // FAB Visibility
    val isFabVisible by remember { derivedStateOf { listState.firstVisibleItemIndex == 0 || !listState.isScrollInProgress } }

    Scaffold(
        modifier = Modifier.nestedScroll(scrollBehavior.nestedScrollConnection),
        topBar = {
            TopAppBar(
                title = { Text(stringResource(StudyStrings.ToolNamePlanner)) },
                navigationIcon = { IconButton(onClick = onBackClick) { Icon(StudyIcons.ArrowBack, stringResource(StudyStrings.Back)) } },
                scrollBehavior = scrollBehavior,
                colors = TopAppBarDefaults.topAppBarColors(containerColor = Color.Transparent)
            )
        },
        floatingActionButton = {
            AnimatedVisibility(visible = isFabVisible, enter = slideInVertically { it } + fadeIn(), exit = slideOutVertically { it } + fadeOut()) {
                StudyFloatingActionButton(
                    onClick = {
                        if (state.selectedTab == 0) viewModel.onEvent(StudyPlannerEvent.ToggleAddSessionDialog(true))
                        else viewModel.onEvent(StudyPlannerEvent.ToggleAddSubjectDialog(true))
                    },
                    icon = StudyIcons.Add,
                    contentDescription = stringResource(StudyStrings.Addbtn)
                )
            }
        }
    ) { padding ->

        LazyColumn(
            state = listState,
            modifier = Modifier.fillMaxSize().padding(padding),
            contentPadding = PaddingValues(bottom = 80.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            // 1. Stats
            item { StatisticsSectionContainer(state.stats) }

            // 2. Active Session
            if (state.activeSessionId != null) {
                item { RealTimeTrackingBanner(state.sessionElapsedTimeSeconds.toInt()) { viewModel.onEvent(StudyPlannerEvent.StopSession) } }
            }

            // 3. Tabs
            stickyHeader {
                SecondaryTabRow(selectedTabIndex = state.selectedTab, containerColor = MaterialTheme.colorScheme.surface) {
                    Tab(
                        selected = state.selectedTab == 0,
                        onClick = {
                            viewModel.onEvent(
                                StudyPlannerEvent.SelectTab(0))
                                  },
                        text = { Text(stringResource(StudyStrings.Sessions))
                               },
                        icon =
                            {
                                Icon(
                                    imageVector = StudyIcons.SessionTab,
                                    contentDescription = stringResource(StudyStrings.Sessions)
                                )
                            }
                    )

                    Tab(
                        selected = state.selectedTab == 1,
                        onClick = { viewModel.onEvent(
                            StudyPlannerEvent.SelectTab(1))
                                  },
                        text =
                            {
                                Text(stringResource(StudyStrings.Subjects))
                            },
                    icon =
                        {
                            Icon(
                                imageVector = StudyIcons.SubjectTab,
                                contentDescription = stringResource(StudyStrings.Subjects)
                            )
                        }
                    )

                }
            }

            // 4. List
            if (state.selectedTab == 0) {
                renderSessionList(state.sessions, state.activeSessionId) { viewModel.onEvent(it) }
            } else {
                items(state.subjects, key = {it.id})
                { subject ->
                    SubjectCard(
                        subject = subject,
                        progress = state.subjectProgressMap[subject.id] ?: 0f,
                        onDeleteClick = { viewModel.onEvent(
                            StudyPlannerEvent.DeleteSubject(it))}
                    )
                }// Define SubjectCard in components if needed
            }
        }
    }

    // Dialogs
    if (state.isAddSessionDialogOpen) {
        AddSessionDialog(
            subjects = state.subjects,
            flashcardSubject = state.flashcardReviews,
            onDismiss = { viewModel.onEvent(StudyPlannerEvent.ToggleAddSessionDialog(false)) },
            onAdd = { viewModel.onEvent(StudyPlannerEvent.CreateSession(it)) }
        )
    }
    if (state.isAddSubjectDialogOpen) {
        AddSubjectDialog(onDismiss = { viewModel.onEvent(StudyPlannerEvent.ToggleAddSubjectDialog(false)) }) {
            viewModel.onEvent(StudyPlannerEvent.CreateSubject(it))
        }
    }
}

// Helper to keep main screen clean
private fun LazyListScope.renderSessionList(
    sessions: List<StudySession>,
    activeId: String?,
    onEvent: (StudyPlannerEvent) -> Unit
) {
    // 1. Handle Empty State
    if (sessions.isEmpty()) {
        item {
            Box(
                Modifier.fillMaxWidth().padding(32.dp),
                contentAlignment = Alignment.Center
            ) {
                Text(stringResource(StudyStrings.messageboSessions))
            }
        }
        return
    }

    // 2. Categorize Sessions
    val active = sessions.filter { it.id == activeId }
    val remaining = sessions.filter { it.id != activeId }

    val inProgress = remaining.filter { !it.completed }
    val completed = remaining.filter { it.completed }

    // 3. Active Section
    if (active.isNotEmpty()) {
        item {
            SessionSectionHeader(
                stringResource(StudyStrings.Active),
                StudyIcons.Active,
                StudyTheme.Error,
                active.size
            )
        }
        items(active) { session ->
            SessionCard(
                session = session,
                isActive = true,
                onStartSession = { /* Already active, do nothing */ },
                onStopSession = { id -> onEvent(StudyPlannerEvent.StopSession) },
                onDeleteClick = { id -> onEvent(StudyPlannerEvent.DeleteSession(id)) }
            )
        }
    }

    // 4. In Progress Section (Fixed: 'inProgress' is now defined)
    if (inProgress.isNotEmpty()) {
        item {
            SessionSectionHeader(
                stringResource(StudyStrings.InProgress),
                StudyIcons.InProgress,
                StudyTheme.Progress,
                inProgress.size
            )
        }
        items(inProgress) { session ->
            SessionCard(
                session = session,
                isActive = false,
                onStartSession = { id -> onEvent(StudyPlannerEvent.StartSession(id)) },
                onStopSession = { /* Cannot stop inactive session */ },
                onDeleteClick = { id -> onEvent(StudyPlannerEvent.DeleteSession(id)) }
            )
        }
    }

    // 5. Completed Section
    if (completed.isNotEmpty()) {
        item {
            SessionSectionHeader(
                stringResource(StudyStrings.Completed),
                StudyIcons.Completed,
                StudyTheme.Completed,
                completed.size
            )
        }
        items(completed) { session ->
            SessionCard(
                session = session,
                isActive = false,
                onStartSession = { id -> onEvent(StudyPlannerEvent.StartSession(id)) }, // Allows restarting/reviewing
                onStopSession = { },
                onDeleteClick = { id -> onEvent(StudyPlannerEvent.DeleteSession(id)) }
            )
        }
    }
}
/*
@Composable
fun SubjectCard(subject: Subject, progress: Float, onDeleteClick: (String) -> Unit) {
    ElevatedCard(
        Modifier.fillMaxWidth()
            .padding(horizontal = 16.dp)
    ) {
        Row(Modifier.padding(16.dp))
        {
            Text(subject.name, style = MaterialTheme.typography.titleMedium)
        }
    }
}*/
